/**
 * Wrappers tipados para /api/admin/* (§12 SRS) + /api/legal/vigente/* (público).
 */

import { apiClient } from './apiClient'

// ─── Dashboard ─────────────────────────────────────────────────────────────
export type AdminMetrics = {
  total_users: number
  total_tenants: number
  reports_last_week: number
  reports_last_month: number
  images_last_week: number
  images_last_month: number
  ai_enhancements_this_month: number
  ai_tokens_in_this_month: number
  ai_tokens_out_this_month: number
  ai_cost_this_month_usd: number
}

export type AdminDashboardResponse = {
  metrics: AdminMetrics
  generated_at: string
}

// ─── Broadcast ─────────────────────────────────────────────────────────────
export type BroadcastStatus = 'active' | 'scheduled' | 'expired' | 'inactive'

export type BroadcastMessage = {
  id: number
  message: string
  start_date: string  // YYYY-MM-DD
  end_date: string
  active: boolean
  status: BroadcastStatus
  created_at: string
}

export type BroadcastPayload = {
  message: string
  start_date: string
  end_date: string
  active?: boolean
}

// ─── Usuarios ──────────────────────────────────────────────────────────────
export type AdminUserListItem = {
  id: number
  tenant_id: number
  email: string
  first_name: string
  last_name_paternal: string
  last_name_maternal: string | null
  full_name: string
  active: boolean
  last_login_at: string | null
  created_at: string
}

export type AdminUserDetail = AdminUserListItem & {
  failed_login_count: number
  locked_until: string | null
  reports_total: number
  plan: {
    id: number
    name: string
    status: string
    billing_type: string
    monthly_price: number
    yearly_price: number
    start_date: string
    current_period_end: string
  } | null
}

export type AdminUsersPage = {
  items: AdminUserListItem[]
  next_cursor: number | null
  total: number
}

export type AdminUserPlanPayload = {
  plan_id: number
  start_date: string  // YYYY-MM-DD
  current_period_end: string  // YYYY-MM-DD
}

// ─── Finanzas (historial de pagos) ────────────────────────────────────────
export type AdminPaymentStatus = 'pending' | 'paid' | 'failed'

// El endpoint de lista y el de detalle devuelven exactamente la misma forma
// (mismo serializador en PaymentsRepository::serializeForAdmin).
export type AdminPaymentListItem = {
  id: number
  tenant_id: number
  tenant_name: string
  user_id: number | null
  user_email: string | null
  user_first_name: string | null
  user_last_name_paternal: string | null
  plan_id: number
  plan_name: string
  reference: string
  bank_confirmation: string | null
  amount: number
  status: AdminPaymentStatus
  created_at: string
  paid_at: string | null
  covers_start_date: string | null
  covers_end_date: string | null
}

export type AdminPaymentDetail = AdminPaymentListItem

export type AdminPaymentsPage = {
  items: AdminPaymentListItem[]
  next_cursor: number | null
  total: number
}

// ─── Planes ────────────────────────────────────────────────────────────────
export type AdminPlan = {
  id: number
  name: string
  description: string | null
  monthly_price: number
  yearly_price: number
  features: string[]
  max_reports_per_month: number
  max_ai_enhancements_per_month: number
  max_clients: number
  max_users: number
  ai_enhancement_enabled: boolean
  active: boolean
  visible: boolean
  recommended: boolean
  is_default: boolean
  created_at: string
}

export type AdminPlanPayload = Partial<{
  name: string
  description: string
  monthly_price: number
  yearly_price: number
  features: string[]
  max_reports_per_month: number
  max_ai_enhancements_per_month: number
  max_clients: number
  max_users: number
  ai_enhancement_enabled: boolean
  active: boolean
  visible: boolean
  recommended: boolean
  is_default: boolean
}>

// ─── Legal ─────────────────────────────────────────────────────────────────
export type LegalType = 'TOS' | 'PP'

export type LegalDocument = {
  id: number
  type: LegalType
  content_html: string
  version: number
  is_current: boolean
  published_at: string
  published_by: number | null
  published_by_name: string | null
  published_by_email: string | null
}

export type LegalPublishPayload = {
  type: LegalType
  content_html: string
}

// ─── Server Config (Claude) ────────────────────────────────────────────────
export type ServerConfigEntry = {
  value: string | null
  is_masked: boolean
  is_set: boolean
}

export type ServerConfigResponse = {
  claude: {
    api_key: ServerConfigEntry
    model: ServerConfigEntry
    system_prompt: ServerConfigEntry
    default_model: string
    default_system_prompt: string
  }
}

export type ServerConfigClaudePayload = Partial<{
  api_key: string
  model: string
  system_prompt: string
}>

// ─── Server Config (Email/SMTP) ────────────────────────────────────────────
export type EmailConfigKey =
  | 'email.smtp_host'
  | 'email.smtp_port'
  | 'email.smtp_user'
  | 'email.smtp_password'
  | 'email.smtp_encryption'
  | 'email.from_address'
  | 'email.from_name'

export type EmailConfigResponse = {
  email: Record<EmailConfigKey, ServerConfigEntry>
}

export type EmailConfigPayload = Partial<{
  smtp_host: string
  smtp_port: number
  smtp_user: string
  smtp_password: string
  smtp_encryption: 'tls' | 'ssl' | 'none'
  from_address: string
  from_name: string
}>

// ─── Email Templates ───────────────────────────────────────────────────────
export type EmailTemplateKey = 'signup_otp' | 'password_reset_otp' | 'payment_confirmed' | 'report_closed' | 'team_invitation'

export type EmailTemplateVariable = {
  key: string
  label: string
  example: string
}

export type EmailTemplate = {
  key: EmailTemplateKey
  subject: string
  body_html: string
  variables: EmailTemplateVariable[]
  updated_at: string
  updated_by: number | null
}

export const EMAIL_TEMPLATE_LABELS: Record<EmailTemplateKey, string> = {
  signup_otp: 'OTP de registro',
  password_reset_otp: 'OTP de recuperación de contraseña',
  payment_confirmed: 'Pago confirmado',
  report_closed: 'Envío de reportes',
  team_invitation: 'Invitación al equipo',
}

// ─── Cliente ───────────────────────────────────────────────────────────────
export const adminClient = {
  // Dashboard
  dashboard: () => apiClient.get<AdminDashboardResponse>('/admin/dashboard'),

  // Broadcast (admin CRUD)
  broadcast: {
    list: () => apiClient.get<{ items: BroadcastMessage[] }>('/admin/broadcast'),
    create: (payload: BroadcastPayload) =>
      apiClient.post<{ item: BroadcastMessage }>('/admin/broadcast', payload),
    update: (id: number, payload: BroadcastPayload) =>
      apiClient.patch<{ item: BroadcastMessage }>(`/admin/broadcast/${id}`, payload),
    remove: (id: number) => apiClient.delete<void>(`/admin/broadcast/${id}`),
  },

  // Usuarios
  users: {
    list: (params: { q?: string; cursor?: number | null; limit?: number } = {}) => {
      const qs = new URLSearchParams()
      if (params.q) qs.set('q', params.q)
      if (params.cursor != null) qs.set('cursor', String(params.cursor))
      qs.set('limit', String(params.limit ?? 25))
      return apiClient.get<AdminUsersPage>(`/admin/usuarios?${qs.toString()}`)
    },
    show: (id: number) => apiClient.get<{ user: AdminUserDetail }>(`/admin/usuarios/${id}`),
    setActive: (id: number, active: boolean) =>
      apiClient.patch<{ user: AdminUserDetail }>(`/admin/usuarios/${id}/active`, { active }),
    updatePlan: (id: number, payload: AdminUserPlanPayload) =>
      apiClient.patch<{ user: AdminUserDetail }>(`/admin/usuarios/${id}/plan`, payload),
  },

  // Finanzas (historial de pagos)
  finanzas: {
    list: (params: { q?: string; status?: AdminPaymentStatus; cursor?: number | null; limit?: number } = {}) => {
      const qs = new URLSearchParams()
      if (params.q) qs.set('q', params.q)
      if (params.status) qs.set('status', params.status)
      if (params.cursor != null) qs.set('cursor', String(params.cursor))
      qs.set('limit', String(params.limit ?? 25))
      return apiClient.get<AdminPaymentsPage>(`/admin/finanzas?${qs.toString()}`)
    },
    show: (id: number) => apiClient.get<{ payment: AdminPaymentDetail }>(`/admin/finanzas/${id}`),
  },

  // Planes
  planes: {
    list: () => apiClient.get<{ items: AdminPlan[] }>('/admin/planes'),
    show: (id: number) => apiClient.get<{ plan: AdminPlan }>(`/admin/planes/${id}`),
    create: (payload: AdminPlanPayload) =>
      apiClient.post<{ plan: AdminPlan }>('/admin/planes', payload),
    update: (id: number, payload: AdminPlanPayload) =>
      apiClient.patch<{ plan: AdminPlan }>(`/admin/planes/${id}`, payload),
  },

  // Legal
  legal: {
    list: () => apiClient.get<{ items: LegalDocument[] }>('/admin/legal'),
    show: (id: number) => apiClient.get<{ document: LegalDocument }>(`/admin/legal/${id}`),
    publish: (payload: LegalPublishPayload) =>
      apiClient.post<{ document: LegalDocument }>('/admin/legal', payload),
  },

  // Server Config
  serverConfig: {
    show: () => apiClient.get<ServerConfigResponse>('/admin/server-config'),
    updateClaude: (payload: ServerConfigClaudePayload) =>
      apiClient.patch<ServerConfigResponse & { changed: string[] }>(
        '/admin/server-config/claude',
        payload,
      ),

    showEmail: () => apiClient.get<EmailConfigResponse>('/admin/server-config/email'),
    updateEmail: (payload: EmailConfigPayload) =>
      apiClient.patch<EmailConfigResponse & { changed: string[] }>(
        '/admin/server-config/email',
        payload,
      ),
    sendTestEmail: (to: string) =>
      apiClient.post<{ sent: true; to: string }>('/admin/server-config/email/test', { to }),
  },

  // Email Templates
  emailTemplates: {
    list: () => apiClient.get<{ items: EmailTemplate[] }>('/admin/email-templates'),
    show: (key: EmailTemplateKey) =>
      apiClient.get<{ template: EmailTemplate }>(`/admin/email-templates/${key}`),
    update: (key: EmailTemplateKey, payload: { subject: string; body_html: string }) =>
      apiClient.patch<{ template: EmailTemplate }>(`/admin/email-templates/${key}`, payload),
    sendTest: (key: EmailTemplateKey, to: string) =>
      apiClient.post<{ sent: true; to: string }>(`/admin/email-templates/${key}/test`, { to }),
  },
}

/**
 * Endpoint público (sin auth) — Términos vigentes / Aviso de Privacidad.
 * Usado por el formulario de registro y la pantalla de Perfil.
 */
export const legalPublicClient = {
  current: (tipo: LegalType) =>
    apiClient.get<{
      document: { type: LegalType; version: number; content_html: string; published_at: string }
    }>(`/legal/vigente/${tipo}`, { skipAuth: true }),
}

export const BROADCAST_STATUS_LABELS: Record<BroadcastStatus, string> = {
  active: 'Vigente',
  scheduled: 'Programado',
  expired: 'Expirado',
  inactive: 'Inactivo',
}

export const LEGAL_TYPE_LABELS: Record<LegalType, string> = {
  TOS: 'Términos de Servicio',
  PP: 'Aviso de Privacidad',
}
